import { useState } from 'react';
import { type TaskLink, linkInfo, parseLinkUrl } from '@quadro/domain';
import { Icon } from '../../components/Icon';

const BADGE = { sharepoint: ['S', 'SharePoint'], onedrive: ['O', 'OneDrive'], teams: ['T', 'Teams'], web: ['', 'Site'] } as const;
export const MAX_LINKS = 30;

/**
 * Links da tarefa: colar o endereço (SharePoint, OneDrive, Teams, qualquer site) e Enter. O nome vem do
 * arquivo ou pasta do endereço e pode ser trocado. Abre em outra aba; o Quadro não lê o conteúdo.
 */
export function LinksField({ value, onChange, editable }: { value: TaskLink[]; onChange: (l: TaskLink[]) => void; editable: boolean }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function add(raw = draft) {
    if (!raw.trim()) return;
    const url = parseLinkUrl(raw);
    if (!url) { setError('Cole um endereço que comece com https://'); return; }
    if (value.some(l => l.url === url)) { setError('Esse link já está na tarefa.'); return; }
    if (value.length >= MAX_LINKS) { setError(`No máximo ${MAX_LINKS} links por tarefa.`); return; }
    onChange([...value, { url, title: linkInfo(url).title }]);
    setDraft(''); setError(null);
  }

  return (
    <div className="lk">
      {value.map((l, i) => {
        const info = linkInfo(l.url);
        const [letter, label] = BADGE[info.kind];
        return (
          <div className="lk-row" key={l.url}>
            <span className={`lk-badge lk-${info.kind}`} title={label}>{letter || <Icon name="link" />}</span>
            {editable
              ? <input className="lk-title" value={l.title} maxLength={200} aria-label="Nome do link"
                  onChange={e => onChange(value.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                  onBlur={() => { if (!l.title.trim()) onChange(value.map((x, j) => (j === i ? { ...x, title: info.title } : x))); }} />
              : <span className="lk-title">{l.title || info.title}</span>}
            <a className="lk-open" href={l.url} target="_blank" rel="noopener noreferrer" title={l.url}>Abrir<span className="lk-host">{info.host}</span></a>
            {editable && <button type="button" className="x-btn" aria-label={`Remover link ${l.title}`} onClick={() => onChange(value.filter((_, j) => j !== i))}><Icon name="x" /></button>}
          </div>
        );
      })}
      {!value.length && !editable && <span className="muted">Nenhum link.</span>}
      {editable && (
        <div className="lk-add">
          <Icon name="link" />
          <input value={draft} placeholder="Colar link do SharePoint, OneDrive, Teams ou site e Enter" aria-label="Novo link" spellCheck={false}
            onChange={e => { setDraft(e.target.value); setError(null); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            onPaste={e => {
              // Colar um link com o campo vazio já adiciona (sem precisar de Enter).
              const text = e.clipboardData.getData('text');
              if (!draft.trim() && parseLinkUrl(text)) { e.preventDefault(); add(text); }
            }} />
          {draft.trim() && <button type="button" className="btn" onClick={() => add()}>Adicionar</button>}
        </div>
      )}
      {error && <span className="lk-err" role="alert">{error}</span>}
    </div>
  );
}
